/**
 * Upcoming — the screen the app opens on.
 *
 * Two filters carried over from the desktop (see visibleMatches for the one correction):
 *
 * - **Top 10 leagues by LIQUIDITY, not volume.** Upcoming matches have ~0 volume; liquidity is the
 *   only real popularity signal before kickoff. Ranking by volume puts the board in the wrong order
 *   and hides the leagues that are actually trading.
 * - **Plus any match with a stored prediction**, whatever its league. A prediction was paid for, so
 *   it never disappears off the board just because its league dropped out of the top ten.
 *
 * Finished matches are excluded regardless — "upcoming" has to mean upcoming, or yesterday's
 * fixtures sit at the top forever.
 *
 * Arrangement, not a fork: one body of state, two layouts. Wide gets two columns and full cards;
 * narrow gets one column of dense cards. Both read the same selection from the store.
 */
import { MatchCard } from '@/components/MatchCard';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Text } from '@/components/ui/text';
import { cn } from '@/lib/utils';
import { hasPrediction, isComplete, isToday, kickoffMs, type Match } from '@/models';
import { stakeKey, useStore } from '@/store';
import { useLayout } from '@/useLayout';
import * as React from 'react';
import { Pressable, ScrollView, View } from 'react-native';

const TOP_LEAGUES_LIMIT = 10;

/**
 * The visible set: top leagues by summed liquidity, plus every predicted match.
 *
 * **The ranking is over matches that have NOT finished**, which is a deliberate correction to the
 * desktop's version. It ranked over every cached match, and because the retention rule keeps a
 * predicted match forever, the cache fills with finished fixtures whose leagues then dominate the
 * sum. On the migrated data that put MLS 2025, Liga MX 2025 and four other finished competitions in
 * the top ten and hid **10 of 19 live fixtures**, USL Championship's whole slate among them. Ranking
 * "the leagues worth showing on an upcoming board" by matches that already kicked off months ago is
 * the bug, not the limit.
 *
 * Everything else is as it was: liquidity, **not volume** — upcoming matches have ~0 volume, so
 * liquidity is the only real popularity signal before kickoff — and a predicted match is always
 * kept, whatever its league, because it was paid for.
 */
export function visibleMatches(matches: Match[], now = Date.now()): Match[] {
  const live = matches.filter((m) => !isComplete(m, now));

  const byLeague = new Map<string, number>();
  for (const m of live) byLeague.set(m.league, (byLeague.get(m.league) ?? 0) + m.liquidity);

  const top = new Set(
    [...byLeague.entries()]
      .sort((a, b) => b[1] - a[1])
      .slice(0, TOP_LEAGUES_LIMIT)
      .map(([league]) => league),
  );

  return matches
    .filter((m) => top.has(m.league) || hasPrediction(m))
    .sort((a, b) => kickoffMs(a) - kickoffMs(b));
}

export function UpcomingScreen() {
  const { wide } = useLayout();
  const {
    matches,
    selectedLeague,
    selectLeague,
    todayOnly,
    toggleToday,
    stakeAll,
    setStakeAll,
    applyStakeToAll,
    stakes,
    placements,
    setStake,
    predict,
    predictAll,
    place,
    refresh,
    loading,
    status,
  } = useStore();
  const [open, setOpen] = React.useState<string | null>(null);

  const visible = React.useMemo(() => visibleMatches(matches).filter((m) => !isComplete(m)), [matches]);

  const shown = React.useMemo(
    () =>
      visible
        .filter((m) => (selectedLeague === null ? true : m.league === selectedLeague))
        .filter((m) => (todayOnly ? isToday(m) : true)),
    [visible, selectedLeague, todayOnly],
  );

  const leagueTabs = React.useMemo(() => {
    const counts = new Map<string, number>();
    for (const m of visible) counts.set(m.league, (counts.get(m.league) ?? 0) + 1);
    return [...counts.entries()].sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]));
  }, [visible]);

  const unpredicted = shown.filter((m) => !hasPrediction(m)).map((m) => m.eventId);

  return (
    <ScrollView contentContainerClassName="gap-3 pb-6">
      <View className="flex-row flex-wrap items-center gap-2">
        <Button variant="outline" size="sm" disabled={loading} onPress={() => void refresh()}>
          <Text>{loading ? 'Refreshing…' : 'Refresh'}</Text>
        </Button>
        <Button
          variant="default"
          size="sm"
          disabled={unpredicted.length === 0}
          onPress={() => void predictAll(unpredicted)}>
          <Text>Run all ({unpredicted.length})</Text>
        </Button>
        <View className="flex-row items-center gap-1">
          <Input
            className="native:h-9 h-9 w-20"
            value={stakeAll}
            onChangeText={setStakeAll}
            placeholder="Stake"
            keyboardType="decimal-pad"
            aria-label="Stake to apply to every shown bet"
          />
          <Button
            variant="secondary"
            size="sm"
            onPress={() => applyStakeToAll(shown.map((m) => m.eventId))}>
            <Text>Apply to all</Text>
          </Button>
        </View>
      </View>

      <Text variant="small" className="text-muted-foreground">
        {status}
      </Text>

      {/* Today is an independent toggle ANDed with the league, not a league tab — same as the
          desktop's separate button before the strip. */}
      <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerClassName="gap-1 pb-1">
        <Chip label="Today" active={todayOnly} onPress={toggleToday} />
        <Chip label={`All (${visible.length})`} active={selectedLeague === null} onPress={() => selectLeague(null)} />
        {leagueTabs.map(([league, count]) => (
          <Chip
            key={league}
            label={`${league} (${count})`}
            active={selectedLeague === league}
            onPress={() => selectLeague(league)}
          />
        ))}
      </ScrollView>

      {shown.length === 0 ? (
        <Empty
          title="Nothing upcoming here"
          body={
            todayOnly
              ? 'No match on the selected league kicks off today. Turn Today off to see the rest.'
              : 'The refresh cron fills this from Polymarket. Press Refresh to pull it now.'
          }
        />
      ) : (
        <View className={cn('gap-3', wide && 'flex-row flex-wrap')}>
          {shown.map((m) => (
            <View key={m.eventId} className={cn(wide && 'min-w-[380px] flex-1 basis-[380px]')}>
              <MatchCard
                match={m}
                dense={!wide}
                expanded={open === m.eventId}
                onToggleExpand={() => setOpen(open === m.eventId ? null : m.eventId)}
                stakes={Object.fromEntries(
                  Object.entries(stakes)
                    .filter(([k]) => k.startsWith(`${m.eventId}:`))
                    .map(([k, v]) => [k.split(':')[1], v]),
                )}
                placement={Object.fromEntries(
                  Object.entries(placements)
                    .filter(([k]) => k.startsWith(`${m.eventId}:`))
                    .map(([k, v]) => [k.split(':')[1], v]),
                )}
                onStake={(key, v) => setStake(m.eventId, key, v)}
                onPredict={() => void predict(m.eventId)}
                onPlace={(key) => void place(m.eventId, key)}
              />
            </View>
          ))}
        </View>
      )}
    </ScrollView>
  );
}

export function Chip({
  label,
  active,
  onPress,
}: {
  label: string;
  active: boolean;
  onPress: () => void;
}) {
  return (
    <Pressable
      onPress={onPress}
      role="tab"
      aria-selected={active}
      className={cn(
        'rounded-full border px-3 py-1.5',
        active ? 'bg-accent border-transparent' : 'border-border web:hover:bg-muted',
      )}>
      <Text
        variant="small"
        className={cn(active ? 'text-accent-foreground font-semibold' : 'text-muted-foreground')}>
        {label}
      </Text>
    </Pressable>
  );
}

/**
 * The honest empty state: it always names the next step. "No items" on its own is
 * indistinguishable from a broken query, which is the failure this exists to avoid.
 */
export function Empty({ title, body }: { title: string; body: string }) {
  return (
    <View className="border-border items-center gap-2 rounded-lg border border-dashed p-8">
      <Text variant="heading">{title}</Text>
      <Text variant="small" className="text-muted-foreground max-w-[40ch] text-center">
        {body}
      </Text>
    </View>
  );
}

export { stakeKey };
